import * as React from 'react';

import { IdTypes, createFeatureScope } from 'bundles/product-features';
import OrgScopedHomeFeatureContext from 'bundles/unified-common/contexts/OrgScopedHomeFeatureContext';
import { useUserExperienceContext } from 'bundles/unified-common/contexts/UserExperienceContext';

const { useProductFeatures } = /* @__PURE__ */ createFeatureScope({
  enterprise: {
    enableOrgScopedHome: false,
  },
});

/**
 * Lightweight provider that fetches only the `enableOrgScopedHome` product feature
 * using the org ID from the user's stored enterprise experience selection.
 *
 * Used inside UnifiedUserExperienceHandler where the full EnterpriseProductFeaturesDataProvider
 * is not available (it requires ThirdPartyOrganizationContext from page-level data providers).
 */
const OrgScopedHomeFeatureProvider = ({ children }: { children: React.ReactNode }) => {
  const { userExperience, isLoading: isUxLoading } = useUserExperienceContext();

  const orgId =
    userExperience?.__typename === 'UserExperienceContext_EnterpriseSelection' ? userExperience.organization.id : '';

  const { features, loading } = useProductFeatures({
    idType: IdTypes.Organization,
    id: orgId,
    skip: !orgId,
    ssr: true,
  });

  const value = React.useMemo(
    () => ({
      enabled: orgId ? features.get('enterprise', 'enableOrgScopedHome') : false,
      isLoading: isUxLoading || loading,
    }),
    [orgId, features, isUxLoading, loading]
  );

  return <OrgScopedHomeFeatureContext.Provider value={value}>{children}</OrgScopedHomeFeatureContext.Provider>;
};

export default OrgScopedHomeFeatureProvider;
